import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '../../shared/utils/cn';
import FadeInImage from '../../shared/components/FadeInImage';
import useHorizontalScroller from '../../shared/hooks/useHorizontalScroller';
import * as API from '../../api';

/**
 * Curated image per category. Pulled from /storage/events/ assets we know exist.
 * Unknown categories fall back to a generic Morocco photo so new categories from
 * the API still render gracefully.
 */
const CATEGORY_IMAGES = {
  'Lifestyle':                   '/storage/events/balade-a-cheval-essaouira-events-1-2021-03-12.jpeg',
  'camping':                     '/storage/events/ait-bouguemez-et-lac-bin-ouidane-events-partner-411-2021-11-29.jpeg',
  'Plages, Lacs...':             '/storage/events/activite-kayak-sale-events-partner-496-2023-04-05.jpeg',
  'Evènements et festivals':     '/storage/events/arc-attack-a-casablanca-events-partner-609-2024-03-12.jpeg',
  'Sensation forte':             '/storage/events/parapente-a-marrakech-events-partner-85-2023-12-20.jpg',
  'Equitation':                  '/storage/events/balade-a-cheval-agadir-events-partner-240-2021-11-26.jpg',
  'Nature':                      '/storage/events/ascension-du-mont-bab-n-ouayad-lacs-islyi-tislit-gorges-assif-melloul-events-partner-378-2021-11-30.jpeg',
  'Personnalisé':                '/storage/events/2j-1-nuit-marrekch-imlil-ourika-chaque-weekend-events-1-2023-11-13.jpeg',
  'Concerts & Festivals':        '/storage/events/arc-attack-a-casablanca-events-partner-609-2024-03-12.jpeg',
  'Théâtre & Humour':            '/storage/events/atelier-d-art-a-casablanca-events-partner-565-2023-11-16.jpeg',
  'Bien‑être':                   '/storage/events/chefchaouen-floatation-center-events-partner-638-2024-04-30.jpeg',
  'Bien-être':                   '/storage/events/chefchaouen-floatation-center-events-partner-638-2024-04-30.jpeg',
  'Ateliers et formations':      '/storage/events/atelier-zellige-events-partner-862-2025-02-06.png',
  'Restauration et Gastronomie': '/storage/events/blue-door-cuisine-a-tanger-events-partner-629-2024-04-19.jpeg',
  'Sport et Aventures':          '/storage/events/activite-de-buggy-a-tanger-events-partner-657-2024-06-07.jpeg',
  'Séjour':                      '/storage/events/appartement-luxueuse-a-bella-vista-cabo-negro-events-partner-86-2024-01-06.JPG',
};

const FALLBACK_IMG = '/storage/events/balade-a-chameau-agafay-events-partner-396-2021-11-28.jpeg';

function imageFor(name) {
  return CATEGORY_IMAGES[name] || FALLBACK_IMG;
}

function CategoryCard({ category }) {
  return (
    <Link
      to={`/events?category=${encodeURIComponent(category.name)}`}
      className="group relative block h-[180px] w-[150px] shrink-0 snap-start overflow-hidden rounded-xl shadow-card transition-all duration-300 ease-out-quint hover:-translate-y-1 hover:shadow-elevated sm:h-[280px] sm:w-[220px] sm:rounded-2xl"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="h-full w-full transition-transform duration-[900ms] ease-out-quint group-hover:scale-[1.08]">
          <FadeInImage
            src={imageFor(category.name)}
            alt={category.name}
            className="h-full w-full object-cover"
          />
        </div>
      </div>
      {/* gradient overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />

      {/* name + arrow at bottom */}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2.5 sm:gap-3 sm:p-4">
        <p className="line-clamp-2 text-[13px] font-semibold leading-tight text-white drop-shadow-sm sm:text-base">
          {category.name}
        </p>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/95 text-brand-500 shadow-card transition-all duration-300 group-hover:bg-accent-500 group-hover:text-brand-900 sm:h-8 sm:w-8">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3 rtl:rotate-180 sm:h-4 sm:w-4">
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

function ScrollButton({ direction, onClick, disabled }) {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      className={cn(
        'hidden h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-ink shadow-card transition-all duration-200 lg:flex',
        'hover:border-brand-500 hover:text-brand-500 hover:shadow-card-hover',
        disabled && 'cursor-not-allowed opacity-40 hover:border-slate-200 hover:text-ink hover:shadow-card'
      )}
    >
      <Icon className="h-5 w-5 rtl:rotate-180" />
    </button>
  );
}

export default function Categories() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { scrollerRef, canPrev, canNext, progress, scrollByDir } =
    useHorizontalScroller([categories.length, loading]);

  useEffect(() => {
    let mounted = true;
    API.AllCategories()
      .then((res) => mounted && setCategories(res?.data || []))
      .catch(() => mounted && setCategories([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <section className="relative bg-surface pt-2 pb-8 sm:pt-4 sm:pb-14">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <h2 className="font-display text-[19px] font-semibold tracking-tight text-ink whitespace-nowrap sm:text-[28px] sm:whitespace-normal lg:text-3xl">
            {t('home.categoriesTitle')}
          </h2>

          <div className="flex items-center gap-2">
            <ScrollButton direction="left" onClick={() => scrollByDir('left')} disabled={!canPrev} />
            <ScrollButton direction="right" onClick={() => scrollByDir('right')} disabled={!canNext} />
          </div>
        </div>

        <div className="relative mt-3 sm:mt-6">
          <div
            ref={scrollerRef}
            className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-2 sm:gap-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-[180px] w-[150px] shrink-0 snap-start animate-pulse rounded-xl bg-slate-100 sm:h-[280px] sm:w-[220px] sm:rounded-2xl"
                  />
                ))
              : categories.map((cat) => <CategoryCard key={cat.id} category={cat} />)}
          </div>
        </div>

        {/* Mobile scroll progress indicator */}
        {!loading && categories.length > 0 && (
          <div className="mt-3 flex items-center justify-center gap-3 sm:mt-4 lg:hidden">
            <div className="relative h-1 w-32 overflow-hidden rounded-full bg-slate-200">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-brand-500 transition-[width] duration-150 rtl:left-auto rtl:right-0"
                style={{ width: `${Math.max(15, progress * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
